// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Types
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export interface TextSegment {
  text: string
  isHighlight: boolean
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Utility Functions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Escapes special regex characters in a string
 */
export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Splits text into segments based on keyword matches
 * @param text - The text to split
 * @param keywords - Array of keywords to highlight
 * @returns Array of text segments with highlight flags
 */
export function splitTextByKeywords(text: string, keywords: string[]): TextSegment[] {
  if (!text || keywords.length === 0) {
    return [{ text, isHighlight: false }]
  }

  // Filter out empty keywords and escape special regex characters
  const validKeywords = keywords
    .filter((k) => k.trim().length > 0)
    .map((k) => escapeRegex(k.trim()))

  if (validKeywords.length === 0) {
    return [{ text, isHighlight: false }]
  }

  // Create a regex pattern that matches any of the keywords (case-insensitive)
  const pattern = validKeywords.join('|')
  const regex = new RegExp(`(${pattern})`, 'gi')

  // Split the text by the regex pattern
  const parts = text.split(regex)

  // Map parts to segments with highlight information
  return parts
    .filter((part) => part.length > 0)
    .map((part) => ({
      text: part,
      isHighlight: validKeywords.some((keyword) => new RegExp(`^${keyword}$`, 'i').test(part)),
    }))
}
